import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageSquare, X, Send, ArrowRight, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QUESTIONS = [
  {
    key: 'name',
    label: 'Full Name',
    message: 'To get started, may I have your full name?',
    type: 'text',
    placeholder: 'Your full name',
    required: true,
    validate: (v) => v.trim().length >= 2 ? null : 'Please enter your full name.',
  },
  {
    key: 'phone',
    label: 'Phone Number',
    message: 'What is the best phone number to reach you?',
    type: 'tel',
    placeholder: '(555) 555-5555',
    required: true,
    validate: (v) => v.replace(/\D/g, '').length >= 10 ? null : 'Please enter a valid phone number.',
  },
  {
    key: 'email',
    label: 'Email Address',
    message: 'What is your email address?',
    type: 'email',
    placeholder: 'email@example.com',
    required: false,
    validate: (v) => !v.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Please enter a valid email address.',
  },
  {
    key: 'location',
    label: 'City & State',
    message: 'What city and state are you located in?',
    type: 'text',
    placeholder: 'City, State',
    required: false,
  },
  {
    key: 'incidentDate',
    label: 'Date of Incident',
    message: 'When did the incident occur?',
    type: 'date',
    placeholder: '',
    required: false,
  },
  {
    key: 'description',
    label: 'Description',
    message: 'Please provide a brief description of what happened.',
    type: 'textarea',
    placeholder: 'Briefly describe the incident...',
    required: false,
  },
  {
    key: 'injured',
    label: 'Injured',
    message: 'Were you injured as a result of this incident?',
    type: 'select',
    options: ['Yes', 'No', 'Not sure'],
    required: false,
  },
];

const TypingDots = () => (
  <div className="flex justify-start">
    <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '0.6s' }} />
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms', animationDuration: '0.6s' }} />
      <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms', animationDuration: '0.6s' }} />
    </div>
  </div>
);

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState('welcome');
  const [currentQ, setCurrentQ] = useState(0);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [formData, setFormData] = useState({});
  const [consent, setConsent] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, phase, scrollToBottom]);

  useEffect(() => {
    if (phase === 'questions' && !isTyping) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [phase, currentQ, isTyping]);

  const addBotMessage = useCallback((text, cb) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { sender: 'bot', text }]);
      setIsTyping(false);
      if (cb) cb();
    }, 500);
  }, []);

  const handleStart = () => {
    setPhase('questions');
    setMessages([
      { sender: 'bot', text: 'Welcome to Rizzi Law Group. I can help gather your information so our team can follow up with you.' },
    ]);
    addBotMessage(QUESTIONS[0].message);
  };

  const handleAnswer = (value) => {
    const question = QUESTIONS[currentQ];
    const trimmed = typeof value === 'string' ? value.trim() : value;

    if (question.required && !trimmed) {
      setFieldError(`${question.label} is required.`);
      return;
    }
    if (question.validate && trimmed) {
      const err = question.validate(trimmed);
      if (err) { setFieldError(err); return; }
    }

    setFieldError('');

    const displayValue = question.type === 'date' && trimmed
      ? new Date(trimmed + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : trimmed || 'Skipped';

    setMessages(prev => [...prev, { sender: 'user', text: displayValue }]);
    setFormData(prev => ({ ...prev, [question.key]: trimmed }));
    setInputValue('');

    if (currentQ < QUESTIONS.length - 1) {
      const nextQ = currentQ + 1;
      setCurrentQ(nextQ);
      addBotMessage(QUESTIONS[nextQ].message);
    } else {
      addBotMessage('Here is a summary of the information you provided. Please review before submitting.', () => {
        setPhase('summary');
      });
    }
  };

  const handleSkip = () => handleAnswer('');

  const handleSubmit = async () => {
    if (!consent) return;
    setPhase('submitting');
    setSubmitError('');

    try {
      const res = await fetch(`${API_URL}/api/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'main_site' }),
      });
      const data = await res.json();
      if (data.success) {
        setPhase('submitted');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'intake_form_submit',
          form_type: 'intake',
        });
      } else {
        setSubmitError('We encountered an issue. Please try again or call us directly at (336) 354-1040.');
        setPhase('summary');
      }
    } catch {
      setSubmitError('We encountered an issue. Please try again or call us directly at (336) 354-1040.');
      setPhase('summary');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && QUESTIONS[currentQ]?.type !== 'textarea') {
      e.preventDefault();
      handleAnswer(inputValue);
    }
  };

  const resetWidget = () => {
    setPhase('welcome');
    setCurrentQ(0);
    setMessages([]);
    setInputValue('');
    setFormData({});
    setConsent(false);
    setFieldError('');
    setSubmitError('');
  };

  const progress = phase === 'welcome' ? 0
    : phase === 'questions' ? ((currentQ) / QUESTIONS.length) * 100
    : 100;

  const currentQuestion = QUESTIONS[currentQ];

  return (
    <>
      {/* Chat Panel */}
      {isOpen && (
        <div
          className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-[9999] w-full sm:w-[400px] h-full sm:h-[600px] sm:max-h-[85vh] flex flex-col bg-white sm:rounded-2xl shadow-2xl overflow-hidden sm:border sm:border-slate-200 font-body"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-[#1e3a8a] px-5 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-white font-heading text-lg font-semibold tracking-wide">Rizzi Law Group</h3>
              <p className="text-blue-200 text-xs mt-0.5">Case Intake</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-blue-200 hover:text-white transition-colors p-1 -mr-1"
              aria-label="Close chat"
              data-testid="chat-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Progress Bar */}
          {phase !== 'welcome' && (
            <div className="h-1 bg-slate-100 flex-shrink-0">
              <div
                className="h-full bg-[#1e3a8a] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
                data-testid="chat-progress"
              />
            </div>
          )}

          {/* Body */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {phase === 'welcome' ? (
              <div className="flex flex-col items-center justify-center h-full px-8 py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center mb-6">
                  <MessageSquare className="h-7 w-7 text-[#1e3a8a]" />
                </div>
                <p className="text-slate-700 text-[15px] leading-relaxed mb-2">
                  Welcome to Rizzi Law Group. I can help gather your information so our team can follow up with you.
                </p>
                <p className="text-slate-400 text-xs mb-8">This will only take a few minutes.</p>
                <button
                  onClick={handleStart}
                  className="bg-[#1e3a8a] text-white px-8 py-3 rounded-lg text-sm font-medium hover:bg-[#172e6e] transition-colors flex items-center gap-2"
                  data-testid="chat-start-btn"
                >
                  Get Started <ArrowRight className="h-4 w-4" />
                </button>
                <p className="text-slate-400 text-[11px] mt-10 leading-relaxed max-w-[260px]">
                  Submitting this form does not create an attorney-client relationship.
                </p>
              </div>
            ) : phase === 'submitted' ? (
              <div className="flex flex-col items-center justify-center h-full px-8 py-12 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mb-6">
                  <Check className="h-7 w-7 text-emerald-600" />
                </div>
                <p className="text-slate-800 text-[15px] font-medium mb-2">
                  Thank you. Your information has been submitted.
                </p>
                <p className="text-slate-500 text-sm leading-relaxed mb-6">
                  A member of our team will contact you shortly.
                </p>
                <p className="text-slate-400 text-[11px] leading-relaxed max-w-[260px]">
                  Submitting this form does not create an attorney-client relationship.
                </p>
                <button
                  onClick={() => { resetWidget(); setIsOpen(false); }}
                  className="mt-8 text-[#1e3a8a] text-sm font-medium hover:underline"
                  data-testid="chat-close-done-btn"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="px-4 py-4 space-y-3">
                {messages.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[80%] px-4 py-2.5 text-[14px] leading-relaxed ${
                        msg.sender === 'user'
                          ? 'bg-[#1e3a8a] text-white rounded-2xl rounded-br-md'
                          : 'bg-slate-100 text-slate-700 rounded-2xl rounded-bl-md'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>
                ))}

                {isTyping && <TypingDots />}

                {/* Summary Panel */}
                {(phase === 'summary' || phase === 'submitting') && !isTyping && (
                  <div className="pt-2 pb-4">
                    <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-2.5">
                      {QUESTIONS.map((q) =>
                        formData[q.key] ? (
                          <div key={q.key} className="flex justify-between items-start gap-4 text-sm">
                            <span className="text-slate-500 flex-shrink-0">{q.label}</span>
                            <span className="text-slate-800 font-medium text-right break-words max-w-[55%]">
                              {q.type === 'date'
                                ? new Date(formData[q.key] + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                                : formData[q.key]}
                            </span>
                          </div>
                        ) : null
                      )}
                    </div>

                    <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="consent-label">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1e3a8a] focus:ring-[#1e3a8a] accent-[#1e3a8a]"
                        data-testid="consent-checkbox"
                      />
                      <span className="text-xs text-slate-600 leading-relaxed">
                        I agree to be contacted by Rizzi Law Group by phone, text, and email regarding my inquiry.
                      </span>
                    </label>

                    {submitError && <p className="text-red-600 text-xs mt-3" data-testid="submit-error">{submitError}</p>}

                    <button
                      onClick={handleSubmit}
                      disabled={!consent || phase === 'submitting'}
                      className={`w-full mt-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        consent && phase !== 'submitting'
                          ? 'bg-[#1e3a8a] text-white hover:bg-[#172e6e]'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="chat-submit-btn"
                    >
                      {phase === 'submitting' ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                        </span>
                      ) : (
                        'Submit Information'
                      )}
                    </button>

                    <p className="text-slate-400 text-[11px] mt-3 text-center leading-relaxed">
                      Submitting this form does not create an attorney-client relationship.
                    </p>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input Area */}
          {phase === 'questions' && !isTyping && currentQuestion && (
            <div className="flex-shrink-0 border-t border-slate-200 px-4 py-3 bg-white safe-area-bottom">
              {fieldError && <p className="text-red-600 text-xs mb-2" data-testid="field-error">{fieldError}</p>}

              {currentQuestion.type === 'select' ? (
                <div className="flex gap-2">
                  {currentQuestion.options.map((opt) => (
                    <button
                      key={opt}
                      onClick={() => handleAnswer(opt)}
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium border border-slate-200 text-slate-700 hover:bg-[#1e3a8a] hover:text-white hover:border-[#1e3a8a] transition-colors active:scale-[0.97]"
                      data-testid={`option-${opt.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              ) : currentQuestion.type === 'textarea' ? (
                <div className="space-y-2">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => { setInputValue(e.target.value); setFieldError(''); }}
                    placeholder={currentQuestion.placeholder}
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#1e3a8a] focus:ring-1 focus:ring-[#1e3a8a] resize-none"
                    data-testid="chat-textarea"
                  />
                  <div className="flex gap-2 justify-end">
                    {!currentQuestion.required && (
                      <button onClick={handleSkip} className="px-4 py-2 text-xs text-slate-400 hover:text-slate-600 transition-colors" data-testid="chat-skip-btn">Skip</button>
                    )}
                    <button
                      onClick={() => handleAnswer(inputValue)}
                      className="bg-[#1e3a8a] text-white px-5 py-2 rounded-lg text-sm hover:bg-[#172e6e] transition-colors flex items-center gap-1.5"
                      data-testid="chat-send-btn"
                    >
                      Send <Send className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 items-center">
                  <input
                    ref={inputRef}
                    type={currentQuestion.type}
                    value={inputValue}
                    onChange={(e) => { setInputValue(e.target.value); setFieldError(''); }}
                    onKeyDown={handleKeyDown}
                    placeholder={currentQuestion.placeholder}
                    className="flex-1 min-w-0 px-4 py-2.5 rounded-lg border border-slate-200 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#1e3a8a] focus:ring-1 focus:ring-[#1e3a8a]"
                    data-testid="chat-input"
                  />
                  {!currentQuestion.required && (
                    <button onClick={handleSkip} className="px-2 py-2.5 text-xs text-slate-400 hover:text-slate-600 transition-colors whitespace-nowrap" data-testid="chat-skip-btn">Skip</button>
                  )}
                  <button
                    onClick={() => handleAnswer(inputValue)}
                    className="bg-[#1e3a8a] text-white p-2.5 rounded-lg hover:bg-[#172e6e] transition-colors flex-shrink-0"
                    aria-label="Send"
                    data-testid="chat-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              )}

              <p className="text-slate-400 text-[11px] mt-2 text-center">
                Question {currentQ + 1} of {QUESTIONS.length}
                {!currentQuestion.required && <span className="text-slate-300 ml-1">(optional)</span>}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-[#1e3a8a] text-white shadow-lg hover:shadow-xl hover:bg-[#172e6e] transition-all flex items-center justify-center group"
          aria-label="Open intake form"
          data-testid="chat-toggle-btn"
        >
          <MessageSquare className="h-6 w-6 transition-transform group-hover:scale-110" />
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-300" />
          </span>
        </button>
      )}
    </>
  );
};

export default ChatWidget;
